"use client";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { BlockConfig } from "@/lib/registry/types";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { Check, ChevronsUpDown, Sparkles } from "lucide-react";
import Link from "next/link";
import { useMemo, useState, useRef, useEffect } from "react";
import { getLibraryColorValue } from "@/lib/colors/library-colors";
import { cn } from "@/lib/utils";

interface AutocompleteSearchProps {
  components: BlockConfig[];
}

export function AutocompleteSearch({ components }: AutocompleteSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Extract unique categories from components
  const categories = useMemo(() => {
    const allCategories = components.flatMap(
      (component) => component.categories || []
    );
    return Array.from(new Set(allCategories)).sort();
  }, [components]);

  // Filter components based on search query and selected category
  const filteredComponents = useMemo(() => {
    let filtered = components;

    // Apply category filter if selected
    if (selectedCategory) {
      filtered = filtered.filter((component) =>
        (component.categories || []).includes(selectedCategory)
      );
    }

    // Apply search query filter
    if (searchQuery) {
      filtered = filtered.filter(
        (component) =>
          component.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          component.description
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (component.categories || []).some((cat) =>
            cat.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          (component.tags || []).some((tag) =>
            tag.toLowerCase().includes(searchQuery.toLowerCase())
          )
      );
    }

    // If no filters, show popular components
    if (!searchQuery && !selectedCategory) {
      return filtered.slice(0, 5);
    }

    return filtered.slice(0, 10);
  }, [components, searchQuery, selectedCategory]);

  // Generate buttons with separators
  const categoryButtons = useMemo(() => {
    const buttons: React.ReactNode[] = [];

    // Take up to 3 categories
    const displayCategories = categories.slice(0, 3);

    // Build the array with separators
    for (let i = 0; i < displayCategories.length; i++) {
      const category = displayCategories[i];

      // Add separator before button (except for first button)
      if (i > 0) {
        buttons.push(<span key={`sep-${category}`}>•</span>);
      }

      // Add the button
      buttons.push(
        <Button
          key={category}
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => {
            setSelectedCategory(category);
            setSearchQuery(""); // Clear the search query
            setIsOpen(true);
          }}
        >
          {category}
        </Button>
      );
    }

    return buttons;
  }, [categories]);

  // Focus input when popover opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  return (
    <div className="relative max-w-2xl mx-auto mt-8 z-10">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            className="w-full justify-between h-12 relative flex items-center border rounded-lg border-input px-3 bg-background shadow-sm"
          >
            <div className="flex items-center w-full">
              <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-muted-foreground mr-2" />
              <span className="flex-grow text-left truncate">
                {selectedCategory
                  ? `Category: ${selectedCategory}${searchQuery ? ` - ${searchQuery}` : ""}`
                  : searchQuery || "Search components..."}
              </span>
            </div>
            <kbd className="pointer-events-none select-none items-center hidden md:inline-flex gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
              <span className="text-xs">⌘</span>K
            </kbd>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[calc(100vw-2rem)] md:w-[660px] max-h-[500px]"
          align="center"
          side="bottom"
          id="search-components"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command className="rounded-lg shadow-md w-full">
            <CommandInput
              placeholder={
                selectedCategory
                  ? `Search in ${selectedCategory}...`
                  : "Search components..."
              }
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="h-12"
              autoFocus
              ref={inputRef}
            />
            <CommandList className="max-h-[400px] overflow-y-auto">
              <CommandEmpty>No components found.</CommandEmpty>

              {selectedCategory && (
                <CommandGroup heading={`Category: ${selectedCategory}`}>
                  <CommandItem
                    onSelect={() => setSelectedCategory(null)}
                    className="cursor-pointer text-blue-500"
                  >
                    Clear category filter
                  </CommandItem>
                </CommandGroup>
              )}

              <CommandGroup heading="Components">
                {filteredComponents.length > 0 ? (
                  filteredComponents.map((component) => {
                    const href = `/components/${component.library}/${component.name}`;
                    return (
                      <CommandItem
                        key={`${component.library}/${component.name}`}
                        value={`${component.library}/${component.name}`}
                        onSelect={() => setIsOpen(false)}
                        className="cursor-pointer"
                      >
                        <Link
                          href={href}
                          onClick={() => setIsOpen(false)}
                          className="block"
                        >
                          <div className="flex items-center">
                            <span
                              className="mr-2 h-4 w-4 rounded-full"
                              style={{
                                backgroundColor: getLibraryColorValue(
                                  component.library as string,
                                  { opacity: 50 }
                                ),
                              }}
                            />
                            <span>{component.name}</span>
                            <span className="ml-2 text-muted-foreground">
                              {component.library}
                            </span>
                          </div>
                        </Link>
                      </CommandItem>
                    );
                  })
                ) : (
                  <CommandItem disabled>No components found.</CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div className="mt-3 flex justify-center gap-2 text-sm text-muted-foreground">
        <span>Try:</span>
        {categoryButtons}
      </div>
    </div>
  );
}
